(() => {
  if (customElements.get('tp-e-product-videos')) return;

  const LOCK_CLASS = 'tp-e-product-videos-locked';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const safePlay = (video, onBlocked) => {
    const promise = video.play();
    if (promise && typeof promise.catch === 'function') {
      promise.catch(() => {
        if (onBlocked) onBlocked();
      });
    }
  };

  class TpEProductVideos extends HTMLElement {
    connectedCallback() {
      this.triggers = Array.from(this.querySelectorAll('[data-video-trigger]'));
      this.previews = Array.from(this.querySelectorAll('[data-video-preview]'));
      this.dialog = this.querySelector('[data-video-dialog]');
      this.stage = this.querySelector('[data-video-stage]');
      this.player = this.querySelector('[data-video-player]');
      this.counter = this.querySelector('[data-video-current]');

      if (!this.triggers.length || !this.dialog || !this.player) return;

      this.currentIndex = 0;
      this.activeTrigger = null;
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.triggers.forEach((trigger, index) => {
        trigger.addEventListener('click', () => this.open(index, trigger), { signal });
      });

      this.querySelector('[data-video-close]')?.addEventListener('click', () => this.close(), { signal });
      this.querySelector('[data-video-prev]')?.addEventListener('click', () => this.show(this.currentIndex - 1), { signal });
      this.querySelector('[data-video-next]')?.addEventListener('click', () => this.show(this.currentIndex + 1), { signal });

      // Clique fora do vídeo e dos controles fecha o modal
      this.dialog.addEventListener(
        'click',
        (event) => {
          if (!event.target.closest('[data-video-stage], button')) this.close();
        },
        { signal }
      );

      this.dialog.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      this.dialog.addEventListener('close', () => this.onClose(), { signal });

      this.setupPreviews();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.observer?.disconnect();
      this.observer = null;

      if (this.dialog?.open) this.dialog.close();
      this.resetPlayer();
      document.documentElement.classList.remove(LOCK_CLASS);
    }

    setupPreviews() {
      const autoplay = this.dataset.previewAutoplay === 'true';
      if (!autoplay || reducedMotion.matches || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(({ isIntersecting, target }) => {
            if (isIntersecting) {
              this.playPreview(target);
            } else {
              target.pause();
            }
          });
        },
        { rootMargin: '100px 0px' }
      );

      this.previews.forEach((preview) => this.observer.observe(preview));
    }

    playPreview(preview) {
      if (this.dialog.open) return;

      if (!preview.getAttribute('src') && preview.dataset.src) {
        preview.src = preview.dataset.src;
      }

      preview.muted = true;
      safePlay(preview);
    }

    pausePreviews() {
      this.previews.forEach((preview) => preview.pause());
    }

    resumePreviews() {
      if (!this.observer) return;

      // Reobservar dispara o callback com a visibilidade atual de cada prévia
      this.observer.disconnect();
      this.previews.forEach((preview) => this.observer.observe(preview));
    }

    open(index, trigger) {
      this.activeTrigger = trigger;
      this.pausePreviews();

      document.documentElement.classList.add(LOCK_CLASS);
      this.dialog.showModal();
      this.show(index);
    }

    show(index) {
      const total = this.triggers.length;
      this.currentIndex = (index + total) % total;

      const trigger = this.triggers[this.currentIndex];
      const { fullSrc, poster, ratio } = trigger.dataset;

      this.player.pause();
      this.player.muted = this.dataset.modalSound !== 'true';
      this.player.loop = this.dataset.modalLoop === 'true';

      if (poster) {
        this.player.poster = poster;
      } else {
        this.player.removeAttribute('poster');
      }

      this.player.setAttribute('aria-label', trigger.getAttribute('aria-label') || '');
      this.stage?.style.setProperty('--pv-ratio', ratio || '0.5625');
      if (this.counter) this.counter.textContent = String(this.currentIndex + 1);

      this.player.src = fullSrc;
      safePlay(this.player, () => {
        // Navegador bloqueou a reprodução com som: inicia sem som, o cliente ativa pelos controles
        this.player.muted = true;
        safePlay(this.player);
      });
    }

    onKeydown(event) {
      if (event.target === this.player || this.triggers.length < 2) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.show(this.currentIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.show(this.currentIndex + 1);
      }
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    onClose() {
      this.resetPlayer();
      document.documentElement.classList.remove(LOCK_CLASS);

      if (this.activeTrigger && document.contains(this.activeTrigger)) {
        this.activeTrigger.focus({ preventScroll: true });
      }

      this.resumePreviews();
    }

    resetPlayer() {
      if (!this.player) return;

      this.player.pause();
      this.player.removeAttribute('src');
      this.player.load();
    }
  }

  customElements.define('tp-e-product-videos', TpEProductVideos);
})();
